// Aba Pessoas do painel Aplicativo: cada conta do app, com filtros, e o painel
// lateral da pessoa (dados, vitrine, históricos e as ações do escritório).
import { useEffect, useState } from 'react'
import { FiSearch, FiX, FiChevronLeft, FiChevronRight, FiDownload, FiLock, FiKey, FiSlash, FiCamera } from 'react-icons/fi'
import { API_APP, useApp, Carregando, Falha, Cabeca, Chip, Selo, Avatar, PAPEL, num, ha, quando, titulo } from './comum'
import { PessoaDrawer } from './PessoaDrawer'

export type Pessoa = {
  codigo: string; nome: string | null; papel: string | null; grupo: string | null; grupo_codigo: string | null
  distrito: string | null; distrito_codigo: string | null; no_avanco: boolean; teste: boolean; whatsapp: string | null
  ativo: boolean; desativado_por: string | null; desativado_em: string | null
  bloqueada: boolean; bloqueado_ate: string | null; falhas_login: number
  trocar_senha: boolean; trocar_pedido_por: string | null; trocar_pedido_em: string | null
  ultimo_uso: string | null; ultimo_login: string | null; usando: boolean; instalado_em: string | null; criado_em: string
  foto: string | null
  whatsapp_app: { numero: string | null; por: string | null; em: string | null } | null
  senha: { origem: 'escolhida' | 'enviada' | 'sem_senha'; trocada_em?: string | null; ultimo_envio?: { em: string; motivo: string; rotulo: string; por: string | null; final: string | null; destino: string } | null }
  vitrine: { url: string; aberturas: number; folhearam: number; chamados: number; pedidos: number; artes: number; aberturas_sempre: number; ultima_visita: string | null } | null
}

type Lista = {
  total: number; pagina: number; por_pagina: number; dias: number
  contagem: Record<string, number>
  distritos: { codigo: string; nome: string }[]; grupos: { codigo: string; nome: string }[]
  pessoas: Pessoa[]
}

export const FILTROS: [string, string, 'neutro' | 'bom' | 'alerta' | 'ruim' | 'info'][] = [
  ['todas', 'Todas', 'neutro'], ['usando', 'Usando', 'bom'], ['paradas', 'Paradas há 7 dias', 'alerta'],
  ['nunca_instalou', 'Não instalaram', 'neutro'], ['sem_foto', 'Sem foto', 'neutro'], ['link_sem_visita', 'Link nunca aberto', 'neutro'],
  ['bloqueadas', 'Bloqueadas', 'ruim'], ['troca_pendente', 'Troca de senha pendente', 'info'], ['desativadas', 'Desativadas', 'neutro'],
]

export function Pessoas({ filtroInicial, gere }: { filtroInicial?: string; gere: boolean }) {
  const [filtro, setFiltro] = useState(filtroInicial || 'todas')
  const [busca, setBusca] = useState('')
  const [buscaViva, setBuscaViva] = useState('')
  const [papel, setPapel] = useState('')
  const [distrito, setDistrito] = useState('')
  const [grupo, setGrupo] = useState('')
  const [ordem, setOrdem] = useState('uso')
  const [pagina, setPagina] = useState(1)
  const [aberta, setAberta] = useState<string | null>(null)

  useEffect(() => { if (filtroInicial) setFiltro(filtroInicial) }, [filtroInicial])
  // A busca espera a pessoa parar de digitar: cada letra não vira uma chamada.
  useEffect(() => { const t = setTimeout(() => setBuscaViva(busca), 350); return () => clearTimeout(t) }, [busca])
  useEffect(() => { setPagina(1) }, [filtro, buscaViva, papel, distrito, grupo, ordem])

  const q = new URLSearchParams({ filtro, ordem, pagina: String(pagina) })
  if (buscaViva.trim()) q.set('busca', buscaViva.trim())
  if (papel) q.set('papel', papel)
  if (distrito) q.set('distrito', distrito)
  if (grupo) q.set('grupo', grupo)
  const { dados, erro, carregando, recarregar } = useApp<Lista>(`${API_APP}/pessoas?${q}`)

  if (!dados) return erro ? <Falha erro={erro} tentar={recarregar} /> : <Carregando />
  const paginas = Math.max(1, Math.ceil(dados.total / dados.por_pagina))
  const sel = 'neu-inset rounded-full px-3 py-1 text-[12px] bg-transparent outline-none max-w-[12rem]'

  return (
    <div className="h-full min-h-0 flex flex-col gap-3 p-4 sm:p-5">
      <Cabeca
        titulo="Pessoas com acesso ao app"
        sub="Toque numa pessoa para ver tudo dela e agir: senha nova, exigir troca, desbloquear, desativar"
        carregando={carregando} recarregar={recarregar}
        chips={FILTROS.map(([k, t, tom]) => (
          (k === 'todas' || dados.contagem[k] > 0 || filtro === k) && (
            <Chip key={k} tom={tom} ativo={filtro === k} onClick={() => setFiltro(k)}>{t} · {num(dados.contagem[k])}</Chip>
          )
        ))}
      />

      <div className="flex items-center gap-2 flex-wrap shrink-0">
        <div className="neu-inset rounded-full flex items-center gap-2 px-3 py-1 w-full sm:w-64">
          <FiSearch className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="nome, código, grupo…" aria-label="Buscar pessoa"
            className="bg-transparent outline-none text-[12.5px] flex-1 min-w-0" />
          {busca && <button onClick={() => setBusca('')} aria-label="Limpar busca"><FiX className="w-3.5 h-3.5 text-muted-foreground" /></button>}
        </div>
        <select value={papel} onChange={e => setPapel(e.target.value)} className={sel} aria-label="Papel">
          <option value="">Todos os papéis</option>
          {Object.entries(PAPEL).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
        </select>
        <select value={distrito} onChange={e => { setDistrito(e.target.value); setGrupo('') }} className={sel} aria-label="Distrito">
          <option value="">Todos os distritos</option>
          {dados.distritos.map(d => <option key={d.codigo} value={d.codigo}>{titulo(d.nome)}</option>)}
        </select>
        <select value={grupo} onChange={e => setGrupo(e.target.value)} className={sel} aria-label="Grupo">
          <option value="">Todos os grupos</option>
          {dados.grupos.map(g => <option key={g.codigo} value={g.codigo}>{titulo(g.nome)}</option>)}
        </select>
        <select value={ordem} onChange={e => setOrdem(e.target.value)} className={sel} aria-label="Ordem">
          <option value="uso">Uso mais recente</option>
          <option value="nome">Nome</option>
          <option value="vitrine">Mais visitas na vitrine</option>
          <option value="entrou">Acesso mais novo</option>
        </select>
        {erro && <span className="text-[11.5px] text-red-600">{erro}</span>}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hidden neu-card rounded-2xl">
        {dados.pessoas.length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground text-center py-10">Ninguém neste filtro.</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {dados.pessoas.map(p => <Linha key={p.codigo} p={p} aoAbrir={() => setAberta(p.codigo)} />)}
          </ul>
        )}
      </div>

      {paginas > 1 && (
        <div className="flex items-center justify-center gap-3 shrink-0 text-[12px]">
          <button disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)} className="neu-relief rounded-full w-8 h-8 inline-flex items-center justify-center disabled:opacity-40" aria-label="Página anterior"><FiChevronLeft /></button>
          <span className="tabular-nums text-muted-foreground">página {pagina} de {paginas} · {num(dados.total)} pessoas</span>
          <button disabled={pagina >= paginas} onClick={() => setPagina(p => p + 1)} className="neu-relief rounded-full w-8 h-8 inline-flex items-center justify-center disabled:opacity-40" aria-label="Próxima página"><FiChevronRight /></button>
        </div>
      )}

      {aberta && <PessoaDrawer codigo={aberta} gere={gere} aoFechar={() => setAberta(null)} aoMudar={recarregar} />}
    </div>
  )
}

function Linha({ p, aoAbrir }: { p: Pessoa; aoAbrir: () => void }) {
  return (
    <li>
      <button type="button" onClick={aoAbrir}
        className="w-full text-left px-3.5 py-2.5 flex items-center gap-3 hover:bg-foreground/[0.03] focus-visible:bg-foreground/[0.05] outline-none">
        {/* A linha já é botão (abre o detalhe, onde a foto amplia). */}
        <Avatar foto={p.foto} nome={p.nome} expandir={false} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[13px] font-semibold truncate max-w-full ${p.ativo ? '' : 'line-through text-muted-foreground'}`} title={p.nome || ''}>{titulo(p.nome) || '(sem nome no Draco)'}</span>
            {!p.ativo && <Selo tom="neutro"><FiSlash className="w-3 h-3" />desativada</Selo>}
            {p.bloqueada && <Selo tom="ruim"><FiLock className="w-3 h-3" />bloqueada</Selo>}
            {p.trocar_senha && <Selo tom="info"><FiKey className="w-3 h-3" />troca pendente</Selo>}
            {p.teste && <Selo>TESTE</Selo>}
            {p.no_avanco && <Selo tom="alerta" title="O cadastro foi para um distrito de depósito do Draco: não entra mais">no depósito</Selo>}
          </div>
          <p className="text-[11px] text-muted-foreground truncate">
            <span className="tabular-nums">{p.codigo}</span>{p.papel && <> · {PAPEL[p.papel] || p.papel}</>}{p.grupo && <> · {titulo(p.grupo)}</>}{p.distrito && <> · {titulo(p.distrito)}</>}
          </p>
        </div>
        <div className="hidden md:flex flex-col items-end gap-0.5 shrink-0 w-32 text-right" title={p.ultimo_uso ? `último uso: ${quando(p.ultimo_uso)}` : 'nunca entrou'}>
          <span className={`text-[12px] font-medium ${p.usando ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>{ha(p.ultimo_uso)}</span>
          <span className="text-[10.5px] text-muted-foreground flex items-center gap-1">
            {p.instalado_em ? <><FiDownload className="w-3 h-3" />instalou</> : 'no navegador'}
            {!p.foto && <><span>·</span><FiCamera className="w-3 h-3 opacity-60" title="sem foto" /></>}
          </span>
        </div>
        <div className="hidden lg:flex flex-col items-end gap-0.5 shrink-0 w-28 text-right" title="Vitrine nos últimos 30 dias">
          <span className="text-[12px] tabular-nums">{p.vitrine ? `${num(p.vitrine.aberturas)} visitas` : 'sem link'}</span>
          <span className="text-[10.5px] text-muted-foreground tabular-nums">{p.vitrine ? `${num(p.vitrine.chamados)} chamados · ${num(p.vitrine.pedidos)} ped.` : ''}</span>
        </div>
        <div className="hidden xl:block shrink-0 w-28 text-right text-[11px] text-muted-foreground">
          {p.senha.origem === 'escolhida' ? 'senha dela' : p.senha.origem === 'enviada' ? 'senha do WhatsApp' : 'sem senha'}
        </div>
        <span className="md:hidden text-[11px] text-muted-foreground shrink-0">{ha(p.ultimo_uso)}</span>
        <FiChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </button>
    </li>
  )
}
